import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	NgZone,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';
import { Chart } from 'chart.js';
import {
	PreviewSlide,
	ReportDateRange
} from '../../models/preview/preview.models';
import {
	ReportContentItem,
	ReportDataSource,
	ReportSettingsService
} from '../../services/report-settings.service';
import { ReportHome } from '../../services/report-home';
import {
	normalizeThemeColor,
	reportDateRange,
	reportDocumentItems
} from '../../services/report-preview-utils';
import { ReportPreviewSlideHostComponent } from '../report-preview-page/components/report-preview-slide-host/report-preview-slide-host.component';
import { ReportTextBlockComponent } from '../report-preview-page/components/report-text-block/report-text-block.component';
import {
	ReportTextBlock,
	isPrintableTextBlock,
	packTextPages
} from '../../services/report-text-blocks';
import { CustomBlockSlideComponent } from '../report-preview-page/slides/custom-block-slide/custom-block-slide.component';
import {
	ReportBlockData,
	ReportBlockSpec,
	ReportBlocksService
} from '../../services/report-blocks.service';
import {
	ReportImage,
	ReportImagesService
} from '../../services/report-images.service';
import {
	imagesAfter,
	imagesAtEnd,
	isGeneratedImage
} from '../../services/report-image-layout';
import { placedGroups } from '../../services/report-placement';
import { ReportImageFigureComponent } from '../report-preview-page/components/report-image-figure/report-image-figure.component';
import { ReportLanguageService } from '../../services/report-language.service';
import { ReportThemeService } from '../../services/report-theme.service';
import { ReportSourceSectionsService } from '../../services/report-source-sections.service';

const EXPORT_CHART_DEVICE_PIXEL_RATIO = 3;
const EXPORT_TIMEOUT_MS = 15000;
/**
 * A text page's room, inside the padding of the page box: 823 less 48 at the
 * top and at the bottom (the stylesheet's `.report-export__text`), and the gap
 * between two blocks on it.
 */
const TEXT_PAGE_ROOM = 823 - 2 * 48;
const TEXT_BLOCK_GAP = 24;

interface ReportExportChart {
	after?: string;
	block: ReportBlockData;
	spec: ReportBlockSpec | undefined;
}

@Component({
	standalone: true,
	selector: 'app-report-preview-export-page',
	templateUrl: './report-preview-export-page.component.html',
	styleUrls: ['./report-preview-export-page.component.scss'],
	imports: [
		NgFor,
		NgIf,
		NgTemplateOutlet,
		ReportTextBlockComponent,
		ReportPreviewSlideHostComponent,
		CustomBlockSlideComponent,
		ReportImageFigureComponent
	],
	providers: [
		ReportLanguageService,
		ReportThemeService,
		ReportSourceSectionsService
	]
})
export class ReportPreviewExportPageComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	items: ReportContentItem[] = [];
	themeColor: string | null = null;
	blocks: ReportBlockData[] = [];
	// The specs `blocks` were resolved from, index for index: a block's data
	// does not say who it is about, and its source line has to.
	blockSpecs: ReportBlockSpec[] = [];
	chartsAfter: Record<string, ReportExportChart[]> = {};
	chartsAtEnd: ReportExportChart[] = [];
	/** Each page's pictures, drawn straight after it, by the page's id. */
	picturesAfter = new Map<string, ReportImage[]>();
	/** The pictures after the charts: unplaced, or after a page not drawn. */
	picturesAtEnd: ReportImage[] = [];
	/** The report's own choice; generated pictures only, never an upload. */
	aiImageLabel = false;
	/** The report's brand, which its fixed sections read; empty on any other home. */
	brandId = '';
	home: ReportHome | null = null;
	dataSource: ReportDataSource | null = null;
	reportId: string | null = null;
	dateRange: ReportDateRange | null = null;
	errorMessage = '';

	/** The reader's text blocks, a page of them at a time once packed. */
	textPagesAfter: Record<string, ReportTextBlock[][]> = {};
	textPagesAtEnd: ReportTextBlock[][] = [];
	/** The same, while they are laid out to be measured. */
	textToMeasure: ReportTextBlock[] = [];
	@ViewChild('textMeasure') private textMeasure?: ElementRef<HTMLElement>;

	private slideSubjects = new Map<string, BehaviorSubject<boolean>>();
	private readonly fontsReady$ = new BehaviorSubject<boolean>(false);
	private readonly assetsReady$ = new BehaviorSubject<boolean>(false);
	// The blocks the reader added are read separately from the sixteen, so the
	// export has to wait for them too — a PDF that renders before they land is
	// a PDF missing the charts the reader asked for.
	private readonly blocksReady$ = new BehaviorSubject<boolean>(false);
	// And for its text to be packed onto pages, which it can only be once the
	// fonts it is measured in have loaded.
	private readonly textReady$ = new BehaviorSubject<boolean>(false);
	// ORDER: before the text is packed — it is measured in the theme's font.
	private readonly themeFontsReady$ = new BehaviorSubject<boolean>(false);
	private readonly destroy$ = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		private settingsService: ReportSettingsService,
		private blocksService: ReportBlocksService,
		private imagesService: ReportImagesService,
		private zone: NgZone,
		private cdRef: ChangeDetectorRef,
		private reportLanguage: ReportLanguageService,
		private reportTheme: ReportThemeService,
		private sourceSections: ReportSourceSectionsService
	) {}

	ngOnInit(): void {
		this.sourceSections.useExportToken();
		(window as any).reportExportMode = true;
		(window as any).reportExportReady = false;
		(window as any).reportExportError = null;
		document.documentElement.setAttribute(
			'data-report-export-ready',
			'false'
		);
		document.body.classList.add('report-export-mode');

		Object.defineProperty(window, 'devicePixelRatio', {
			get: () => EXPORT_CHART_DEVICE_PIXEL_RATIO,
			configurable: true
		});

		Chart.defaults.animation = false as any;
		Chart.defaults.animations = {} as any;

		setTimeout(() => this.markExportReady('timeout'), EXPORT_TIMEOUT_MS);

		Promise.race([
			document.fonts?.ready ?? Promise.resolve(),
			this.wait(4000)
		]).then(() => {
			console.log('[report-export] fonts-ready');
			this.fontsReady$.next(true);
		});

		const reportId =
			this.route.snapshot.paramMap.get('reportId') ?? 'export-preview';

		this.settingsService
			.getReport(reportId, { bypassAuth: true })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: report => {
					if (!report) {
						this.markExportError('Report settings were not found.');
						return;
					}
					this.brandId = report.brandId ?? '';
					this.home = report.home;
					this.dataSource = report.settings.source ?? null;
					this.reportId = report.id;
					// ORDER: before the first detectChanges below draws a slide.
					this.reportLanguage.use(report.settings.language);
					this.dateRange = reportDateRange(
						report.settings,
						new Date().toISOString()
					);
					this.items = reportDocumentItems(report.settings);

					this.themeColor = normalizeThemeColor(
						report.settings.optionalFeatures?.reportColor
					);
					// ORDER: before the first detectChanges below — every slide is
					// created then, and this page does not redraw after it.
					this.reportTheme.apply(
						report.settings.optionalFeatures?.theme,
						this.themeColor
					);
					this.reportTheme.setBrandKit(report.brandKit);
					Promise.race([
						this.reportTheme.loadFonts(
							`${report.settings.coverTitle} ${report.settings.coverDescription}`
						),
						this.wait(4000)
					]).then(() => this.themeFontsReady$.next(true));

					this.placePictures(report.settings.images ?? []);
					this.placeText([]);
					this.aiImageLabel = Boolean(
						report.settings.optionalFeatures?.aiImageLabel
					);

					this.items.forEach(item => {
						this.slideSubjects.set(
							item.id,
							new BehaviorSubject<boolean>(false)
						);
					});

					this.loadBlocks(
						report.id,
						report.settings.blocks ?? [],
						report.settings.language
					);
					// A block nobody typed into prints nothing, not a blank.
					this.textToMeasure = (
						report.settings.textBlocks ?? []
					).filter(isPrintableTextBlock);

					console.log(
						'[report-export] settings-loaded, expecting',
						this.items.length,
						'slides'
					);

					this.cdRef.detectChanges();
					this.startReadinessMonitor();
					combineLatest([this.fontsReady$, this.themeFontsReady$])
						.pipe(
							filter(ready => ready.every(Boolean)),
							take(1),
							takeUntil(this.destroy$)
						)
						.subscribe(() => this.packText());
				},
				error: error => {
					console.error('Report export settings load failed', error);
					this.markExportError(
						error instanceof Error
							? error.message
							: 'Failed to load report settings for export.'
					);
				}
			});
	}

	ngAfterViewInit(): void {
		this.cdRef.detach();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	imageSrc(image: ReportImage): string {
		return this.imagesService.srcFor(image);
	}

	showsAiLabel(image: ReportImage): boolean {
		return this.aiImageLabel && isGeneratedImage(image);
	}

	/**
	 * Where each picture prints: after the page the reader put it after, when
	 * that page is in the file, and after the charts otherwise — the editor's
	 * rule (`report-image-layout`), so the file is the document they arranged.
	 * Worked out once: this page draws only when told to.
	 */
	private placePictures(images: ReportImage[]): void {
		const shown = new Set(this.items.map(item => item.id));
		this.picturesAfter = new Map(
			this.items.map(item => [
				item.id,
				imagesAfter(images, item.id, shown)
			])
		);
		this.picturesAtEnd = imagesAtEnd(images, shown);
	}

	onSlideBuilt(slide: PreviewSlide): void {
		const subject = this.slideSubjects.get(slide.slideType);
		if (subject && !subject.value) {
			console.log('[report-export] slide-ready:', slide.slideType);
			subject.next(true);
		}
	}

	/**
	 * The reader's own blocks, for the file they download.
	 *
	 * There is no session here — Gotenberg puts a short-lived export token on
	 * every request the headless browser makes — so the read goes out with the
	 * interceptor told to leave the Authorization header alone.
	 *
	 * A failure marks the blocks ready rather than failing the export: a PDF
	 * missing one chart is worth more to the reader than no PDF at all.
	 */
	private loadBlocks(
		reportId: string,
		specs: ReportBlockSpec[],
		language: string
	): void {
		this.blockSpecs = specs;
		this.placeCharts();
		if (!specs.length || !this.dateRange) {
			this.blocksReady$.next(true);
			return;
		}

		this.blocksService
			.resolveFor(
				reportId,
				specs,
				{ start: this.dateRange.start, end: this.dateRange.end },
				{ bypassAuth: true, language }
			)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => of([] as ReportBlockData[]))
			)
			.subscribe(blocks => {
				this.blocks = blocks;
				this.placeCharts();
				console.log('[report-export] blocks-ready', blocks.length);
				this.cdRef.detectChanges();
				this.blocksReady$.next(true);
			});
	}

	/**
	 * Lay the text blocks onto pages by the height each one was drawn at, then
	 * draw the pages. Measured in the export's own layout and fonts, so what
	 * was measured is what prints.
	 */
	private packText(): void {
		const measure = this.textMeasure?.nativeElement;
		const blocks = this.textToMeasure;
		if (measure && blocks.length) {
			const heights = new Map(
				blocks.map((block, index) => [
					block,
					measure.children[index]?.getBoundingClientRect().height ?? 0
				])
			);
			this.placeText(blocks, heights);
			this.textToMeasure = [];
			this.cdRef.detectChanges();
		}
		console.log(
			'[report-export] text-ready',
			this.textPagesAtEnd.length +
				Object.values(this.textPagesAfter).reduce(
					(pages, placed) => pages + placed.length,
					0
				)
		);
		this.textReady$.next(true);
	}

	// TRAP: packed one place at a time. Packed together, one page would print
	// the text of two places, and some of it out of its place.
	private placeText(
		blocks: ReportTextBlock[],
		heights = new Map<ReportTextBlock, number>()
	): void {
		const pack = (list: ReportTextBlock[]): ReportTextBlock[][] =>
			packTextPages(
				list.map(block => heights.get(block) ?? 0),
				TEXT_PAGE_ROOM,
				TEXT_BLOCK_GAP
			).map(page => page.map(index => list[index]));
		const placed = placedGroups(
			blocks,
			this.items.map(item => item.id)
		);
		this.textPagesAfter = Object.fromEntries(
			Object.entries(placed.after).map(([place, list]) => [
				place,
				pack(list)
			])
		);
		this.textPagesAtEnd = pack(placed.end);
	}

	private placeCharts(): void {
		const placed = placedGroups(
			this.blocks.map(
				(block, index): ReportExportChart => ({
					after: this.blockSpecs[index]?.after,
					block,
					spec: this.blockSpecs[index]
				})
			),
			this.items.map(item => item.id)
		);
		this.chartsAfter = placed.after;
		this.chartsAtEnd = placed.end;
	}

	private startReadinessMonitor(): void {
		const slideStreams = Array.from(this.slideSubjects.values());

		if (!slideStreams.length) {
			this.assetsReady$.next(true);
			return;
		}

		const allSlidesReady$ = combineLatest(slideStreams).pipe(
			map(values => values.every(v => v))
		);

		// When all slides ready → redraw charts, wait for images → set assetsReady
		allSlidesReady$
			.pipe(
				filter(ready => ready),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				console.log('[report-export] all-slides-ready');
				this.zone.runOutsideAngular(() => {
					this.forceChartRedraw();
					this.waitForImages()
						// ORDER: after the slides draw — a script the first font read
						// did not need, a Cyrillic heading in a Brief report, is only
						// fetched once its text is on the page.
						.then(() =>
							Promise.race([
								document.fonts?.ready ?? Promise.resolve(),
								this.wait(4000)
							])
						)
						.then(() => this.wait(600))
						.then(() => {
							this.forceChartRedraw();
							return this.wait(600);
						})
						.then(() => {
							this.forceChartRedraw();
							return this.wait(200);
						})
						.then(() => {
							console.log('[report-export] assets-ready');
							this.assetsReady$.next(true);
						});
				});
			});

		// Final: all slides + fonts + assets + the reader's own blocks and text
		combineLatest([
			...slideStreams,
			this.fontsReady$,
			this.themeFontsReady$,
			this.assetsReady$,
			this.blocksReady$,
			this.textReady$
		])
			.pipe(
				map(values => values.every(v => v)),
				filter(ready => ready),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe(() => this.markExportReady('all-ready'));
	}

	private waitForImages(): Promise<void> {
		const imgs = Array.from(
			document.querySelectorAll<HTMLImageElement>('img')
		);
		const pending = imgs.filter(img => !img.complete);
		if (!pending.length) return Promise.resolve();

		return Promise.race([
			Promise.all(
				pending.map(
					img =>
						new Promise<void>(resolve => {
							img.addEventListener('load', () => resolve(), {
								once: true
							});
							img.addEventListener('error', () => resolve(), {
								once: true
							});
						})
				)
			).then(() => undefined),
			this.wait(4000)
		]);
	}

	private forceChartRedraw(): void {
		Object.values(Chart.instances).forEach(chart => {
			try {
				const { canvas } = chart;
				canvas.style.width = '';
				canvas.style.height = '';
				chart.options.devicePixelRatio =
					EXPORT_CHART_DEVICE_PIXEL_RATIO;
				chart.options.maintainAspectRatio = false;
				chart.stop();
				chart.resize();
				chart.update('none');
			} catch {
				// ignore destroyed or partially-initialised instances
			}
		});
	}

	private wait(milliseconds: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, milliseconds));
	}

	private markExportError(message: string): void {
		this.errorMessage = message;
		(window as any).reportExportError = message;
		this.markExportReady('error');
		this.cdRef.detectChanges();
	}

	private markExportReady(reason: string): void {
		const win = window as any;
		if (win.reportExportReady === true) return;
		win.reportExportReady = true;
		document.documentElement.setAttribute(
			'data-report-export-ready',
			'true'
		);
		console.log('[report-export] ready=true', reason);
	}
}
